import React, { useState } from "react";
import * as m from "../../lib/model";
import { Landmark } from "lucide-react";

const LTCG = 0.238, QSBS_PARTIAL = 0.318;
const tierFor = (hold) => (hold >= 5 ? 1 : hold >= 4 ? 0.75 : hold >= 3 ? 0.5 : 0);
const pct = (n) => (isNaN(n) ? "n/a" : `≈${(n || 0).toFixed(1)}%`);
const bps = (a, b) => (isNaN(a) || isNaN(b) ? "—" : `+${Math.round((a - b) * 100)} bps`);

export function qsbsEconomics(M, check) {
  // The illustration must tie to the deal's own returns matrix: the commitment is the LP's total
  // invested capital (close + every later call), so gross proceeds are simply check x the deal LP MOIC.
  const full = m.lpWaterfall(M), hold = m.effectiveHold(M), tier = tierFor(hold);
  const f = full.lpEquity > 0 ? check / full.lpEquity : 0;
  const wf = { lpTotal: full.lpTotal * f, lpMoic: full.lpMoic,
               rows: full.rows.map((r) => ({ lpYear: (r.lpYear - r.called) * f })) };
  const day0 = full.lpEquity0 * f;
  const gain = Math.max(0, wf.lpTotal - check);
  const cap = Math.max(15e6, 10 * check);
  const excluded = Math.min(gain, cap) * tier;
  const taxNo = gain * LTCG;
  const taxQ = (gain - excluded) * (tier < 1 ? QSBS_PARTIAL : LTCG);
  const flows = wf.rows.map((r) => r.lpYear);
  const after = (tax) => { const g = [...flows]; g[g.length - 1] -= tax; return m.computeIRR([-day0, ...g]); };
  return { wf, hold, tier, gain, excluded, taxNo, taxQ, netNo: wf.lpTotal - taxNo, netQ: wf.lpTotal - taxQ,
           irrNo: after(taxNo), irrQ: after(taxQ), saved: taxNo - taxQ,
           // What §1202 is worth to the LP in after-tax MOIC terms.
           bumpX: check > 0 ? (taxNo - taxQ) / check : 0 };
}

// What §1202 is worth to the LP against a fully taxed investor: turns of after-tax MOIC and basis points
// of after-tax IRR. The headline LP MOIC/IRR are pre-tax, so under QSBS the LP effectively keeps them.
export function qsbsBump(M) {
  const check = Math.round(m.lpWaterfall(M).lpEquity || 0);
  if (check < 1) return null;
  const q = qsbsEconomics(M, check);
  const bps = Math.round(((q.irrQ || 0) - (q.irrNo || 0)) * 100);
  if (!(q.bumpX > 0.005) && bps < 1) return null;
  return { x: q.bumpX, bps, afterTaxNo: q.netNo / check, afterTaxQ: q.netQ / check,
           irrNo: q.irrNo, irrQ: q.irrQ, unlevered: m.isUnlevered(M) };
}

export function QsbsBlock({ M, minCommit = 25000, note, ask, deck = false }) {
  // Three illustrations: two typical tickets and the whole equity check.
  const fullCheck = Math.round(m.lpWaterfall(M).lpEquity || ask || 0);
  const options = [[500000, m.fmt$(500000)], [1000000, m.fmt$(1000000)],
                   [fullCheck, `Entire equity check · ${m.fmt$(fullCheck)}`]]
    .filter(([v]) => v > 0 && v >= Math.min(minCommit, 500000));
  const [check, setCheck] = useState(1000000);  // on paper the $1M case leads, with all three summarised below
  const isFull = check === fullCheck;
  const q = qsbsEconomics(M, check);
  const holdTxt = M.holdIndefinite ? "an indefinite hold" : `the ${M.hold}-year hold`;
  const body = note || `Investor equity is issued as original-issue stock of a domestic C-corporation with under $75,000,000 of gross assets, structured to qualify as Qualified Small Business Stock under IRC §1202. Stock issued after 4 July 2025 earns a 50% federal gain exclusion after 3 years, 75% after 4 and 100% after 5 — up to the greater of $15,000,000 or 10× basis per investor. On ${holdTxt} the LP profit below may be federally tax-free; a §1045 rollover preserves the clock if we exit early.`;
  const exclTxt = `${Math.round(q.tier * 100)}% exclusion`;
  const rows = [
    [isFull ? "LP equity (entire check)" : "LP investment", m.fmt$(check), m.fmt$(check), "—"],
    [`Gross proceeds (${m.fmtX(q.wf.lpMoic)})`, m.fmt$(q.wf.lpTotal), m.fmt$(q.wf.lpTotal), "—"],
    ["Realized long-term gain", m.fmt$(q.gain), m.fmt$(q.gain), "—"],
    [`Federal tax on gain (LTCG + NIIT 23.8%${q.tier > 0 && q.tier < 1 ? "; 31.8% on taxable part" : ""})`, <span className="neg">{m.fmt$(q.taxNo)}</span>, q.taxQ > 0 ? m.fmt$(q.taxQ) : "$0", `+${m.fmt$(q.saved)}`],
    ["Net after-tax proceeds", <b>{m.fmt$(q.netNo)}</b>, <b>{m.fmt$(q.netQ)}</b>, <b data-testid="ir-qsbs-saved">+{m.fmt$(q.saved)}</b>, "hl"],
    ["Effective after-tax MOIC", m.fmtX(q.netNo / check), <b>{m.fmtX(q.netQ / check)}</b>, `+${m.fmtX((q.netQ - q.netNo) / check)}`],
    ["Effective net IRR (approx.)", pct(q.irrNo), <b>{pct(q.irrQ)}</b>, bps(q.irrQ, q.irrNo)],
  ];
  if (m.isUnlevered(M) && q.bumpX > 0.005) {
    rows.push(["Unlevered LP return with the QSBS bump",
      <span>{m.fmtX(q.netNo / check)} after tax</span>,
      <b>{m.fmtX(q.wf.lpMoic)} headline = after tax</b>,
      <b data-testid="ir-qsbs-bump">+{m.fmtX(q.bumpX)}</b>, "hl"]);
  }
  return (
    <div className="ir-gap" data-testid="ir-qsbs">
      <div className="section-label">The QSBS advantage</div>
      <div className="ir-callout" data-testid="ir-qsbs-advantage">
        <span className="ir-callout-h"><Landmark size={12} /> §1202 qualified small business stock</span>
        {body}
      </div>
      <div className="ir-qsbs-tiers" data-testid="ir-qsbs-tiers">
        {[[3, "50%"], [4, "75%"], [5, "100%"]].map(([y, x]) => (
          <div className={`ir-tier ${(q.hold >= 5 ? 5 : q.hold) === y ? "on" : ""}`} key={y} data-testid={`ir-qsbs-tier-${y}`}><b>{x}</b><span>gain excluded after {y} yrs</span></div>
        ))}
      </div>

      <div className="ir-deck-head ir-gap">
        <span className="ir-eyebrow">QSBS — LP economics</span>
        <h2 className="ir-deck-h" data-testid="ir-qsbs-headline">Illustrative {isFull ? `${m.fmt$(check)} entire equity check` : `${m.fmt$(check)} LP commitment`}: after-tax proceeds under QSBS vs. non-QSBS</h2>
      </div>
      {!deck && (
        <div className="ir-chips" data-testid="ir-qsbs-checks">
          {options.map(([v, label]) => <button key={v} className={`ir-chipbtn ${v === check ? "on" : ""}`} onClick={() => setCheck(v)} data-testid={`ir-qsbs-check-${v}`}>{label}</button>)}
        </div>
      )}
      <div className="ir-assume" data-testid="ir-qsbs-assumptions">
        <span className="ir-eyebrow">Assumptions</span>
        {m.fmt$(check)} {isFull ? "entire equity check" : "LP commitment"} ({(check / (fullCheck || 1) * 100).toFixed(1)}% of the {m.fmt$(fullCheck)} LP equity) • {m.fmtX(q.wf.lpMoic)} MOIC gross — the same LP MOIC as the deal returns matrix → {m.fmt$(q.wf.lpTotal)} gross proceeds ({m.fmt$(q.gain)} gain) • {M.holdIndefinite ? "25-year illustration" : `${M.hold}-year hold`} • Federal LTCG 20% + NIIT 3.8% = 23.8% • QSBS stock issued after July 4, 2025 • Gain {q.gain <= Math.max(15e6, 10 * check) ? "fully within" : "capped at"} the $15,000,000 / 10× basis cap • State tax excluded from illustration
      </div>
      <div className="table-scroll">
        <table className="tbl ir-qsbs-tbl" data-testid="ir-qsbs-econ">
          <thead><tr><th /><th className="num">Without QSBS</th><th className="num gold">With QSBS ({exclTxt})</th><th className="num">LP advantage</th></tr></thead>
          <tbody>
            {rows.map(([l, a, b, c, cls], i) => <tr key={i} className={cls || ""}><td><b>{l}</b></td><td className="num">{a}</td><td className="num">{b}</td><td className="num pos">{c}</td></tr>)}
          </tbody>
        </table>
      </div>
      {deck && (
        <>
          <div className="section-label ir-gap">Every commitment size</div>
          <div className="table-scroll">
            <table className="tbl ir-qsbs-tbl" data-testid="ir-qsbs-sizes">
              <thead><tr><th>LP commitment</th><th className="num">Gross proceeds</th><th className="num">Net after tax — no QSBS</th><th className="num gold">Net after tax — QSBS</th><th className="num">LP advantage</th></tr></thead>
              <tbody>
                {options.map(([v, label]) => {
                  const e = qsbsEconomics(M, v);
                  return (
                    <tr key={v} data-testid={`ir-qsbs-size-${v}`}>
                      <td><b>{label}</b></td>
                      <td className="num">{m.fmt$(e.wf.lpTotal)}</td>
                      <td className="num">{m.fmt$(e.netNo)} ({m.fmtX(e.netNo / v)})</td>
                      <td className="num"><b>{m.fmt$(e.netQ)}</b> ({m.fmtX(e.netQ / v)})</td>
                      <td className="num pos">+{m.fmt$(e.saved)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="ir-foot-note">Illustrative only. Actual results depend on realized MOIC, hold period, LP tax profile, state taxes, and satisfaction of §1202 corporate-level requirements. Not tax advice — LPs should consult their tax advisor.</div>
    </div>
  );
}
